import React from 'react';
import PropTypes from 'prop-types';

import {LoadingSpinner} from '@albert-io/atomic';
import {refreshSession} from '@albert-io/mandark/authentication';

import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import {UserModelV2} from 'resources/augmented/User/UserModel.v2';
import {getBaseUserQuery} from 'client/User/UserQueryUtil';
import NewStudentOnboarding from 'client/SignUp/NewSignUp/Onboarding/Student/NewStudentOnboarding';
import NewEducatorOnboarding from 'client/SignUp/NewSignUp/Onboarding/Educator/NewEducatorOnboarding';
import AccountTypeSelectionPrompt from 'client/SignUp/NewSignUp/Onboarding/AccountTypeSelectionPrompt';
import {SIGNUP_TASKS} from 'client/SignUp/NewSignUp/Onboarding/onboarding.types';
import sessionStore from 'client/Session/SessionStore';
import {invalidateInterest} from 'resources/mandark.resource';
import {redirect} from 'client/User/UserRedirectUtil';
import {history} from 'client/history';

function getUserQuery() {
  return getBaseUserQuery(sessionStore.getUserId()).include('standard_set_creators_v1');
}

/**
 * Routes the user through onboarding based on `pendingSignupTasks` from the GraphQL API,
 * rendering the appropriate step until signup is complete, then renders its children.
 */
class ForceOnboarding extends React.Component {
  static propTypes = {
    user: PropTypes.instanceOf(UserModelV2).isRequired,
    onFinalStepComplete: PropTypes.func,
    pendingSignupTasks: PropTypes.arrayOf(PropTypes.string),
    signupComplete: PropTypes.bool,
    pendingTasksLoading: PropTypes.bool,
    isTeacher: PropTypes.bool,
    children: PropTypes.node
  };

  static defaultProps = {
    pendingSignupTasks: [],
    signupComplete: false,
    pendingTasksLoading: false,
    isTeacher: false,
    onFinalStepComplete: () => {
      refreshSession().then(() => {
        const {pathname} = history.getCurrentLocation();
        global.scroll(0, 0);
        if (pathname === '/') {
          redirect();
        }
      });
    }
  };

  render() {
    const onComplete = () => {
      invalidateInterest(getUserQuery().done());
    };

    // New flow: use pendingSignupTasks from GraphQL to determine routing
    if (this.props.pendingTasksLoading) {
      return (
        <div className='flex h-screen items-center justify-center'>
          <LoadingSpinner size={3} />
        </div>
      );
    }

    // If signup is complete via the new system, render the app
    if (this.props.signupComplete) {
      return this.props.children;
    }

    const pendingTasks = this.props.pendingSignupTasks;
    const isAccountTypeSelectionPending = pendingTasks.includes(
      SIGNUP_TASKS.ACCOUNT_TYPE_SELECTION
    );

    // if `account_type_selection` is pending (eg: user signed up with old flow and logged in with new flow on), need to choose account type.
    if (isAccountTypeSelectionPending) {
      return <AccountTypeSelectionPrompt />;
    }

    if (pendingTasks.length > 0) {
      const onboardingProps = {
        user: this.props.user,
        onComplete,
        onFinalStepComplete: this.props.onFinalStepComplete
      };

      return this.props.isTeacher ? (
        <NewEducatorOnboarding {...onboardingProps} />
      ) : (
        <NewStudentOnboarding {...onboardingProps} />
      );
    }

    return this.props.children;
  }
}

export default awaitMandarkQueries(
  () => ({
    queries: {
      user: getUserQuery()
    }
  }),
  ForceOnboarding
);
